import React, { useContext, useMemo } from "react";

import { useLocalization } from "@fluent/react";
import {
  Button,
  Chip,
  Checkbox,
  IconButton,
  Table,
  Tooltip,
} from "@narmi/design_system";
import cc from "classcat";

import { getConfidenceChip } from "../confidence";
import { formatDelta, formatReceiptAmount, formatReceiptDate } from "../format";

import {
  RECEIPTS_BULK_SELECTION_LIMIT,
  SelectionContext,
} from "./SelectionContext";
import styles from "./ReceiptsTable.module.scss";

import type { SelectionState } from "./SelectionContext";
import type { ReceiptListRow, SuggestedMatch } from "../hooks/useReceiptsList";

type SetSelectionState = React.Dispatch<React.SetStateAction<SelectionState>>;

const HEADER_LABEL_CLASS = "fontColor--primary fontWeight--bold fontSize--s";

interface ReceiptsTableProps {
  rows: ReceiptListRow[];
  onRowClick?: (uuid: string) => void;
  onAcceptMatch?: (params: {
    suggestionId: number;
    receiptUuid: string;
  }) => void;
  onRejectMatch?: (params: {
    suggestionId: number;
    receiptUuid: string;
  }) => void;
  /** Suggestion ID currently being accepted — disables Accept/Reject for its row. */
  pendingAcceptSuggestionId?: number | null;
  /** Suggestion ID currently being rejected — disables Accept/Reject for its row. */
  pendingRejectSuggestionId?: number | null;
  /** When false, the per-row + header checkbox column is hidden. Defaults
   * to true to preserve existing call-sites; the receipts page passes
   * false on the Matched/Unmatched tabs since bulk actions only apply to
   * Pending Review. */
  showSelection?: boolean;
  /** Per-row failure messages from a bulk action, keyed by receipt uuid.
   * Rows with an entry render an error chip + tooltip in place of the
   * action buttons. Cleared by the parent on the next list refetch. */
  bulkActionErrors?: Record<string, string>;
  highlightIds?: ReadonlySet<string>;
}

const stopPropagation = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

const toggleRowSelection = (
  setSelectionState: SetSelectionState,
  row: ReceiptListRow
) => {
  setSelectionState((previous) => {
    if (previous[row.uuid]) {
      const { [row.uuid]: _omit, ...rest } = previous;
      return rest;
    }
    if (Object.keys(previous).length >= RECEIPTS_BULK_SELECTION_LIMIT) {
      return previous;
    }
    return { ...previous, [row.uuid]: row };
  });
};

// Header reflects the same scope select-all writes to (first N within the cap),
// not a count, so it can't show "checked" while a visible row is still empty.
const toggleVisibleSelection = (
  setSelectionState: SetSelectionState,
  rows: ReceiptListRow[],
  allVisibleSelected: boolean
) => {
  setSelectionState((previous) => {
    if (allVisibleSelected) {
      const next = { ...previous };
      rows.forEach((row) => {
        delete next[row.uuid];
      });
      return next;
    }
    // `rows` is already capped at RECEIPTS_BULK_SELECTION_LIMIT by the
    // caller (selectableRows). Add every row — previously-selected rows
    // that overlap with `rows` must not reduce the budget available for
    // the remaining visible rows.
    const next = { ...previous };
    rows.forEach((row) => {
      next[row.uuid] = row;
    });
    return next;
  });
};

interface ReceiptCellProps {
  primary: string;
  date: string;
  amount: string;
  isEntering?: boolean;
}

const StackedTextCell = ({
  primary,
  date,
  amount,
  isEntering = false,
}: ReceiptCellProps) => {
  const secondaryParts = [date, amount].filter(Boolean);
  return (
    <div
      className={cc([
        styles["stacked-cell"],
        { [styles["row-enter"]]: isEntering },
      ])}
    >
      <span className="fontColor--primary fontWeight--semibold fontSize--s">
        {primary}
      </span>
      {secondaryParts.length > 0 && (
        <span className="fontColor--secondary fontSize--s">
          {secondaryParts.join(" · ")}
        </span>
      )}
    </div>
  );
};

interface SuggestedMatchCellProps {
  match: SuggestedMatch | null;
  emptyLabel: string;
}

const SuggestedMatchCell = ({ match, emptyLabel }: SuggestedMatchCellProps) => {
  if (!match || !match.description) {
    return (
      <span className="fontColor--secondary fontSize--s">{emptyLabel}</span>
    );
  }
  const date = formatReceiptDate(match.date);
  const amount = formatReceiptAmount(match.amount);
  const secondary = [date, amount].filter(Boolean).join(" · ");
  const delta = formatDelta(match.delta);
  return (
    <div className={styles["stacked-cell"]}>
      <span className="fontColor--primary fontWeight--semibold fontSize--s">
        {match.description}
      </span>
      <div className={styles["secondary-row"]}>
        {secondary && (
          <span className="fontColor--secondary fontSize--s">{secondary}</span>
        )}
        {delta && <Chip kind={delta.kind} label={delta.display} />}
      </div>
    </div>
  );
};

const ReceiptsTable = ({
  rows,
  onRowClick,
  onAcceptMatch,
  onRejectMatch,
  pendingAcceptSuggestionId,
  pendingRejectSuggestionId,
  showSelection = true,
  bulkActionErrors,
  highlightIds,
}: ReceiptsTableProps) => {
  const { l10n } = useLocalization();
  const { selectionState, setSelectionState } = useContext(SelectionContext);

  // Rows without a suggested_match can't be bulk-accepted — exclude them from
  // the selectable set so the snackbar count ("Accept N") matches the payload
  // size handleBulkAccept actually sends.
  const selectableRows = useMemo(
    () =>
      rows
        .filter((row) => row.suggested_match?.id != null)
        .slice(0, RECEIPTS_BULK_SELECTION_LIMIT),
    [rows]
  );
  const visibleSelectedCount = useMemo(
    () => rows.filter((row) => selectionState[row.uuid]).length,
    [rows, selectionState]
  );
  const allVisibleSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectionState[row.uuid]);
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  return (
    <div
      className={styles["receipts-table-wrapper"]}
      data-testid="receipts-table"
    >
      <Table
        colVisibility={
          showSelection ? ["*", "*", "m", "m", "*"] : ["*", "m", "m", "*"]
        }
        colLayout={
          showSelection
            ? {
                s: "auto 1fr auto",
                m: "auto 1fr 1fr auto auto",
                l: "auto 1fr 1fr auto auto",
              }
            : {
                s: "1fr auto",
                m: "1fr 1fr auto auto",
                l: "1fr 1fr auto auto",
              }
        }
        rowDensity="compact"
      >
        <Table.Header>
          <Table.Row>
            {showSelection && (
              <Table.HeaderCell>
                <div className={styles["checkbox-cell"]}>
                  <Checkbox
                    kind="table"
                    label=""
                    aria-label={l10n.getString("receipts-table-select-all")}
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={() =>
                      toggleVisibleSelection(
                        setSelectionState,
                        selectableRows,
                        allVisibleSelected
                      )
                    }
                    isDisabled={selectableRows.length === 0}
                    testId="receipts-table-select-all"
                  />
                </div>
              </Table.HeaderCell>
            )}
            <Table.HeaderCell>
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-table-receipt")}
              </span>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-table-suggested-match")}
              </span>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <span className={styles["confidence-header"]}>
                <span className={HEADER_LABEL_CLASS}>
                  {l10n.getString("receipts-table-confidence")}
                </span>
                <Tooltip
                  text={l10n.getString("receipts-confidence-tooltip")}
                  side="top"
                  maxWidth="260px"
                >
                  <IconButton
                    name="info"
                    label={l10n.getString("receipts-confidence-tooltip-label")}
                  />
                </Tooltip>
              </span>
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="end">
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-table-actions")}
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row) => {
            const confidence = getConfidenceChip(row.confidence_score);
            const suggestionId = row.suggested_match?.id ?? null;
            const hasSuggestedMatch = suggestionId !== null;
            const isAcceptable = hasSuggestedMatch && Boolean(onAcceptMatch);
            const isRejectable = hasSuggestedMatch && Boolean(onRejectMatch);
            const isRowPending =
              hasSuggestedMatch &&
              (pendingAcceptSuggestionId === suggestionId ||
                pendingRejectSuggestionId === suggestionId);
            const isRejectPending =
              hasSuggestedMatch && pendingRejectSuggestionId === suggestionId;
            const isRowSelected = Boolean(selectionState[row.uuid]);
            const handleAccept =
              isAcceptable && suggestionId !== null && onAcceptMatch
                ? (event: React.MouseEvent) => {
                    event.stopPropagation();
                    onAcceptMatch({
                      suggestionId,
                      receiptUuid: row.uuid,
                    });
                  }
                : stopPropagation;
            const handleReject =
              isRejectable && suggestionId !== null && onRejectMatch
                ? (event: React.MouseEvent) => {
                    event.stopPropagation();
                    onRejectMatch({
                      suggestionId,
                      receiptUuid: row.uuid,
                    });
                  }
                : stopPropagation;

            return (
              <Table.Row
                key={row.uuid}
                onRowClick={onRowClick ? () => onRowClick(row.uuid) : undefined}
              >
                {showSelection && (
                  <Table.Cell>
                    <div
                      data-testid={`receipts-table-row-${row.uuid}`}
                      onClick={stopPropagation}
                      role="presentation"
                    >
                      <Checkbox
                        kind="table"
                        label=""
                        aria-label={l10n.getString("receipts-table-select", {
                          merchant:
                            row.merchant ??
                            l10n.getString("receipts-table-unknown-merchant"),
                        })}
                        checked={isRowSelected}
                        onChange={() =>
                          toggleRowSelection(setSelectionState, row)
                        }
                        isDisabled={!hasSuggestedMatch}
                      />
                    </div>
                  </Table.Cell>
                )}
                <Table.Cell>
                  <StackedTextCell
                    primary={
                      row.merchant ??
                      l10n.getString("receipts-table-unknown-merchant")
                    }
                    date={formatReceiptDate(row.transaction_date)}
                    amount={formatReceiptAmount(row.amount)}
                    isEntering={highlightIds?.has(row.uuid)}
                  />
                </Table.Cell>
                <Table.Cell>
                  <SuggestedMatchCell
                    match={row.suggested_match}
                    emptyLabel={l10n.getString("receipts-table-no-match")}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Chip
                    kind={confidence.kind}
                    label={l10n.getString(confidence.labelKey)}
                  />
                </Table.Cell>
                <Table.Cell textAlign="end">
                  {bulkActionErrors?.[row.uuid] ? (
                    // tabIndex + aria-label make the chip's failure message
                    // reachable for keyboard / screen-reader users. The
                    // focusable element must sit inside the Tooltip so the
                    // Tooltip's focus listener actually fires — wrapping
                    // it on the outside lets keyboard focus land without
                    // surfacing the error text.
                    <Tooltip text={bulkActionErrors[row.uuid]}>
                      <span
                        onClick={stopPropagation}
                        tabIndex={0}
                        role="button"
                        aria-label={bulkActionErrors[row.uuid]}
                        data-testid={`receipts-table-bulk-error-${row.uuid}`}
                      >
                        <Chip
                          kind="warn"
                          label={l10n.getString("receipts-bulk-row-error")}
                        />
                      </span>
                    </Tooltip>
                  ) : (
                    hasSuggestedMatch && (
                      <div
                        className={styles["row-actions"]}
                        onClick={stopPropagation}
                        role="presentation"
                      >
                        <Button
                          kind="secondary"
                          size="xs"
                          label={l10n.getString("receipts-action-reject")}
                          onClick={handleReject}
                          disabled={!isRejectable || isRowPending}
                          isLoading={isRejectPending}
                          testId={`receipts-table-reject-${row.uuid}`}
                        />
                        <Button
                          kind="tonal"
                          size="xs"
                          label={l10n.getString("receipts-action-accept")}
                          onClick={handleAccept}
                          disabled={!isAcceptable || isRowPending}
                          isLoading={
                            suggestionId !== null &&
                            pendingAcceptSuggestionId === suggestionId
                          }
                          testId={`receipts-table-accept-${row.uuid}`}
                        />
                      </div>
                    )
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

export default ReceiptsTable;
